import { useEffect, useState } from "react";
import { Play, X } from "lucide-react";
import { api, fileUrl } from "@/lib/api";

export default function Gallery() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);

  useEffect(() => {
    api
      .get("/gallery")
      .then(({ data }) => setItems(data))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="gallery-page">
      <section className="bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 md:px-8 py-20 md:py-24">
          <p className="text-sm tracking-[0.18em] uppercase text-sky-600">Gallery</p>
          <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl tracking-tight text-slate-900 max-w-3xl leading-[1.05]">
            A look inside the clinic
          </h1>
          <p className="mt-6 text-lg text-slate-600 max-w-2xl leading-relaxed">
            Treatment rooms, equipment and results — photographed at the clinic, not bought from a stock
            library.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-8 py-20">
        {loading ? (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-64 rounded-2xl bg-slate-200/60 animate-pulse" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <div
            className="border border-dashed border-slate-300 rounded-2xl py-24 text-center"
            data-testid="gallery-empty-state"
          >
            <p className="font-display text-2xl text-slate-800">Photos coming soon</p>
            <p className="mt-3 text-sm text-slate-500 max-w-md mx-auto">
              Our team is adding clinic photos and videos. Please check back shortly.
            </p>
          </div>
        ) : (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="gallery-grid">
            {items.map((item) => (
              <button
                key={item.id}
                onClick={() => setActive(item)}
                data-testid={`gallery-item-${item.id}`}
                className="group relative text-left rounded-2xl overflow-hidden bg-white border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:-translate-y-1 hover:shadow-[0_20px_40px_rgb(0,0,0,0.08)] transition-transform transition-shadow duration-300"
              >
                {item.media_type === "image" ? (
                  <img
                    src={fileUrl(item.storage_path)}
                    alt={item.caption || "Clinic photo"}
                    className="w-full h-64 object-cover"
                    loading="lazy"
                  />
                ) : (
                  <div className="relative">
                    <video
                      src={fileUrl(item.storage_path)}
                      className="w-full h-64 object-cover bg-slate-900"
                      preload="metadata"
                      muted
                    />
                    <span className="absolute inset-0 grid place-items-center">
                      <span className="grid place-items-center w-14 h-14 rounded-full bg-white/90 text-blue-900">
                        <Play className="w-5 h-5 ml-0.5 fill-current" />
                      </span>
                    </span>
                  </div>
                )}
                {item.caption && (
                  <p className="px-6 py-4 text-sm text-slate-700">{item.caption}</p>
                )}
              </button>
            ))}
          </div>
        )}
      </section>

      {active && (
        <div
          className="fixed inset-0 z-[60] bg-slate-950/85 backdrop-blur-sm grid place-items-center p-6"
          onClick={() => setActive(null)}
          data-testid="gallery-lightbox"
        >
          <button
            className="absolute top-6 right-6 text-white/80 hover:text-white"
            onClick={() => setActive(null)}
            data-testid="gallery-lightbox-close"
            aria-label="Close"
          >
            <X className="w-7 h-7" />
          </button>
          <div className="max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
            {active.media_type === "image" ? (
              <img
                src={fileUrl(active.storage_path)}
                alt={active.caption || "Clinic photo"}
                className="w-full max-h-[78vh] object-contain rounded-2xl"
              />
            ) : (
              <video
                src={fileUrl(active.storage_path)}
                className="w-full max-h-[78vh] rounded-2xl bg-black"
                controls
                autoPlay
              />
            )}
            {active.caption && <p className="mt-4 text-center text-sm text-slate-200">{active.caption}</p>}
          </div>
        </div>
      )}
    </div>
  );
}
